import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { Plus, Edit, Trash2, X, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCreationsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [creations, setCreations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCreation, setEditingCreation] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'bouquets',
    image_url: '',
    is_featured: false,
    order: 0
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCreations();
  }, []);

  const fetchCreations = async () => {
    try {
      const response = await axios.get(`${API}/creations`);
      setCreations(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des créations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (creation = null) => {
    if (creation) {
      setEditingCreation(creation);
      setFormData({
        title: creation.title,
        description: creation.description,
        category: creation.category,
        image_url: creation.image_url,
        is_featured: creation.is_featured,
        order: creation.order
      });
    } else {
      setEditingCreation(null);
      setFormData({
        title: '',
        description: '',
        category: 'bouquets',
        image_url: '',
        is_featured: false,
        order: creations.length
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCreation(null);
    setFormData({
      title: '',
      description: '',
      category: 'bouquets',
      image_url: '',
      is_featured: false,
      order: 0
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const response = await axios.post(
          `${API}/upload/base64`,
          { image: reader.result },
          { headers: getAuthHeaders() }
        );
        setFormData({ ...formData, image_url: `${BACKEND_URL}${response.data.url}` });
        toast.success('Image uploadée avec succès');
      } catch (error) {
        toast.error("Erreur lors de l'upload de l'image");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image_url) {
      toast.error("Veuillez ajouter une image");
      return;
    }

    try {
      if (editingCreation) {
        await axios.put(
          `${API}/creations/${editingCreation.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Création mise à jour');
      } else {
        await axios.post(
          `${API}/creations`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Création ajoutée');
      }
      handleCloseModal();
      fetchCreations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette création ?')) return;

    try {
      await axios.delete(`${API}/creations/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Création supprimée');
      fetchCreations();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const categories = [
    { value: 'bouquets', label: 'Bouquets' },
    { value: 'evenements', label: 'Événements' },
    { value: 'mariages', label: 'Mariages' }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-[#3D2E35]">Créations</h1>
            <p className="text-[#6B5A62] mt-1">Gérez votre galerie de créations florales</p>
          </div>
          <Button
            onClick={() => handleOpenModal()}
            className="btn-tevaite rounded-xl text-white"
          >
            <Plus size={20} className="mr-2" />
            Ajouter
          </Button>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#8B4B7C] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : creations.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl">
            <ImageIcon className="mx-auto text-[#E8A5C4]" size={48} />
            <p className="text-[#6B5A62] mt-4">Aucune création pour le moment</p>
            <Button
              onClick={() => handleOpenModal()}
              className="mt-4 btn-tevaite rounded-xl text-white"
            >
              Ajouter votre première création
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {creations.map((creation) => (
              <div key={creation.id} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                <div className="aspect-square relative">
                  <img
                    src={creation.image_url}
                    alt={creation.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 flex space-x-2">
                    <button
                      onClick={() => handleOpenModal(creation)}
                      className="p-2 bg-white rounded-lg shadow-md hover:bg-[#FAF8F5] transition-colors"
                    >
                      <Edit size={16} className="text-[#8B4B7C]" />
                    </button>
                    <button
                      onClick={() => handleDelete(creation.id)}
                      className="p-2 bg-white rounded-lg shadow-md hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </button>
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 bg-white/90 rounded-full text-xs font-medium text-[#8B4B7C]">
                      {categories.find(c => c.value === creation.category)?.label}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-lg text-[#3D2E35]">{creation.title}</h3>
                  <p className="text-sm text-[#6B5A62] mt-1 line-clamp-2">{creation.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#3D2E35]">
                {editingCreation ? 'Modifier la création' : 'Nouvelle création'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Image *</Label>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-[#E8A5C4]/50 rounded-xl p-4 text-center cursor-pointer hover:border-[#C45B9F] transition-colors"
                >
                  {formData.image_url ? (
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="py-8">
                      <Upload className="mx-auto text-[#C45B9F]" size={32} />
                      <p className="text-[#6B5A62] mt-2">Cliquez pour uploader une image</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <div className="text-center">
                  <span className="text-xs text-[#6B5A62]">ou</span>
                </div>
                <Input
                  placeholder="URL de l'image"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="border-[#E8A5C4]/30"
                />
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Nom de la création"
                  required
                  className="border-[#E8A5C4]/30"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description de la création"
                  required
                  rows={3}
                  className="border-[#E8A5C4]/30"
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="border-[#E8A5C4]/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Actions */}
              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 btn-tevaite text-white"
                >
                  {editingCreation ? 'Enregistrer' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCreationsPage;
